import { APP_BASE_HREF, PlatformLocation, registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import localeNl from '@angular/common/locales/nl';
import {
  type ApplicationConfig,
  ErrorHandler,
  inject,
  LOCALE_ID,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import {
  provideRouter,
  withComponentInputBinding,
  withEnabledBlockingInitialNavigation,
  withRouterConfig,
  withViewTransitions,
} from '@angular/router';
import { provideMissingTranslationHandler, provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { ENVIRONMENT_SETTINGS, provideSupportEmailAddress } from '@reconcept-platform/env';
import { ErrorSinkService, provideFeatureToggle, setLanguageGuard } from '@reconcept-platform/shared/app/core';
import {
  AUTH_TOKEN_SERVICE,
  getGraphqlUrlFactory,
  provideAuthTokensFromEnv,
  RedirectFromParamsGuard,
  RedirectToLoginGuard,
  SuccessfulRedirectGuard,
  WebAuthTokenService,
  WebSessionGuard,
} from '@reconcept-platform/shared/authentication';
import {
  ApolloGraphqlConfigService,
  introspectionQueryResultData,
  WorkspaceTypePolicies,
} from '@reconcept-platform/shared/data-access';
import {
  accessTokenListenerGuard,
  otherUserListenerGuard,
} from '@reconcept-platform/shared/ui/standalone/access-token-expired';
import { initBugsnag, RcpMissingTranslationHandlerService } from '@reconcept-platform/shared/util';
import { provideModalOpeners } from '@reconcept-platform/workspace/app/init';
import { workspaceViewerOnboardedRedirectGuard } from '@reconcept-platform/workspace/data-access';
import { provideNamedApollo } from 'apollo-angular';
import { NotFoundComponent } from './not-found.component';

registerLocaleData(localeNl);

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection(),
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    provideRouter(
      [
        // successful return after authorization
        {
          path: 'login',
          children: [
            {
              // this path should match the redirect uri in environment and server side Application
              path: 'success',
              canActivate: [SuccessfulRedirectGuard],
              data: {
                title: 'Succesvolle doorverwijzing',
                internalRedirectPath: ['dashboard'],
              },
              children: [],
            },

            // Any other login path, redirect to auth app (backward compatibility)
            {
              path: '**',
              pathMatch: 'full',
              canActivate: [RedirectToLoginGuard],
              children: [], //required
            },
          ],
        },

        {
          path: 'password-reset/:token',
          pathMatch: 'full',
          canActivate: [RedirectToLoginGuard],
          children: [], //required
        },

        // The welcome path should redirect to the login app (backward compatibility)
        {
          path: 'welcome/:token',
          pathMatch: 'full',
          canActivate: [RedirectToLoginGuard],
          children: [], //required
        },

        {
          path: 'onboarding',
          canActivate: [setLanguageGuard],
          loadChildren: () =>
            import('@reconcept-platform/workspace/account-onboarding/web').then((module) => module.routes),
        },
        {
          path: 'unsubscribe-learning-resource-alert-mails/:token',
          data: { pageStyle: 'standalone' },
          loadComponent: () => import('@reconcept-platform/shared/unsubscribe-learning-resource-alert-mails/web'),
        },
        {
          path: '',
          canMatch: [WebSessionGuard],
          canActivate: [
            // order matters
            // sessions
            WebSessionGuard,
            accessTokenListenerGuard,
            // redirects
            RedirectFromParamsGuard,
            otherUserListenerGuard,
            // viewer
            setLanguageGuard,
            workspaceViewerOnboardedRedirectGuard,
          ],
          loadChildren: () => import('@reconcept-platform/workspace/app/web').then((module) => module.routes),
        },

        { path: '**', component: NotFoundComponent },
      ],
      withViewTransitions(),
      withComponentInputBinding(),
      withEnabledBlockingInitialNavigation(),
      withRouterConfig({
        paramsInheritanceStrategy: 'always',
      }),
    ),
    provideAppInitializer(() => {
      const initializerFn = initBugsnag(inject(ENVIRONMENT_SETTINGS));
      return initializerFn();
    }),
    {
      provide: APP_BASE_HREF,
      useFactory: (s: PlatformLocation) => s.getBaseHrefFromDOM(),
      deps: [PlatformLocation],
    },
    { provide: ErrorHandler, useClass: ErrorSinkService },
    { provide: LOCALE_ID, useValue: 'nl-NL' },
    provideTranslateService({
      fallbackLang: 'en',
      loader: provideTranslateHttpLoader({ prefix: './assets/i18n/', suffix: '.json' }),
      missingTranslationHandler: provideMissingTranslationHandler(RcpMissingTranslationHandlerService),
    }),

    { provide: AUTH_TOKEN_SERVICE, useClass: WebAuthTokenService },
    ...provideAuthTokensFromEnv(),

    provideNamedApollo(() => {
      const environmentSettings = inject(ENVIRONMENT_SETTINGS);
      const generalConfig = inject(ApolloGraphqlConfigService);

      return {
        [environmentSettings.portfolioClientName]: {
          ...generalConfig.create(
            environmentSettings.portfolioClientName,
            getGraphqlUrlFactory(),
            introspectionQueryResultData,
            WorkspaceTypePolicies,
          ),
        },
      };
    }),

    // The providers here are initialized in the order they appear in this list, which is why order matters here,
    // as this provider requires the graphQlService.
    provideFeatureToggle(),
    provideModalOpeners(),
    provideSupportEmailAddress('support@kwaliteitsdashboard.app'),
  ],
};
